import { CARE_LENS, getCareLensKey } from '../../utils/careLens';
import { normalizeChapterProvenance } from './chapterProvenance';
import { PROFILE_CHAPTER_TITLES } from '../../constants/profileChapterTitles';

export const GOALS_DOMAIN_ID = 'DM_08';

const GROUPS = Object.freeze({
  current_priorities: 'Current priorities',
  communication_participation: 'Communication and participation',
  daily_living_independence: 'Daily living and independence',
  health_comfort_wellbeing: 'Health, comfort, and wellbeing',
  temporary_objectives: 'Temporary care objectives',
  long_term_aspirations: 'Long-term aspirations',
  completed_goals: 'Completed goals',
  no_longer_active: 'No longer active',
});

const DEFAULT_GROUP_ORDER = Object.freeze(Object.keys(GROUPS));
const SAFE_SOURCE_LABELS = Object.freeze({
  iep: 'IEP',
  'aba-program': 'ABA program',
  'speech-therapy': 'Speech therapy',
  'occupational-therapy': 'Occupational therapy',
  'home-goal': 'Home goal',
  'medical-team': 'Medical team',
  parent: 'Parent',
  'care-team': 'Care team',
});

const CATEGORY_GROUPS = Object.freeze({
  communication: 'communication_participation',
  'social-skills': 'communication_participation',
  'academic-skills': 'communication_participation',
  transitions: 'daily_living_independence',
  'self-care': 'daily_living_independence',
  'motor-skills': 'daily_living_independence',
  independence: 'daily_living_independence',
  'emotional-regulation': 'health_comfort_wellbeing',
  'sensory-processing': 'health_comfort_wellbeing',
});

const cleanText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();
const normalizeToken = (value = '') => cleanText(value).toLowerCase().replace(/[\s-]+/g, '_');
const goalValueKey = (goal = {}) => cleanText(goal.title).toLowerCase();

const resolveGoalState = (goal = {}) => {
  const status = normalizeToken(goal.status || 'active');
  const kind = normalizeToken(goal.goalType || goal.type || goal.kind);
  if (['archived', 'deleted', 'draft', 'pending', 'needs_review'].includes(status)) return 'excluded';
  if (['completed', 'complete'].includes(status)) return 'completed';
  if (['stopped', 'abandoned', 'replaced', 'paused', 'inactive', 'no_longer_active'].includes(status)) return 'stopped';
  if (status === 'temporary' || ['temporary', 'temporary_objective'].includes(kind)) return 'temporary';
  if (['aspiration', 'aspirational'].includes(status) || ['aspiration', 'aspirational', 'long_term_aspiration'].includes(kind)) return 'aspiration';
  if (['active', 'current', 'approved', 'confirmed'].includes(status)) return 'current';
  return 'excluded';
};

const isConfirmedGoal = (goal = {}) => {
  if (!cleanText(goal.title)) return false;
  if (goal.archived || goal.deleted || goal.isPrivate || goal.protected || goal.safeForCaregiver === false) return false;
  if (goal.confirmed === false || goal.approved === false || goal.needsReview === true) return false;
  if (['draft', 'pending', 'needs_review'].includes(normalizeToken(goal.reviewState || goal.reviewStatus))) return false;
  if ((goal.aiSuggested || goal.suggestedByAI || normalizeToken(goal.originType) === 'ai_suggestion')
    && goal.confirmed !== true && goal.approved !== true) return false;
  return resolveGoalState(goal) !== 'excluded';
};

const classifyCurrentGoal = (goal = {}, state = 'current') => {
  if (state === 'temporary') return 'temporary_objectives';
  if (state === 'aspiration') return 'long_term_aspirations';
  const category = cleanText(goal.category).toLowerCase();
  if (CATEGORY_GROUPS[category]) return CATEGORY_GROUPS[category];
  const text = cleanText(`${goal.title} ${goal.description} ${goal.successCriteria}`).toLowerCase();
  if (/\b(?:communicat|aac|speech|request|choice|participat|school|community|relationship|social|connect)\b/.test(text)) return 'communication_participation';
  if (/\b(?:dress|toilet|brush|self-care|daily living|independen|transfer|walk|routine|household|personal care)\b/.test(text)) return 'daily_living_independence';
  if (/\b(?:comfort|health|wellbeing|well-being|sleep|nutrition|fluid|pain|treatment|calm|quality of life)\b/.test(text)) return 'health_comfort_wellbeing';
  return '';
};

const getGoalProvenance = (goal = {}) => {
  const refs = [];
  const confirmer = goal.confirmedBy || goal.approvedBy;
  const confirmerLabel = cleanText(
    typeof confirmer === 'object' ? confirmer.label || confirmer.name || confirmer.displayName : ''
  );
  if (confirmerLabel) {
    refs.push({ sourceId: 'goal-confirmer', sourceTitle: confirmerLabel });
  }
  const sourceKey = cleanText(goal.source).toLowerCase();
  if (SAFE_SOURCE_LABELS[sourceKey]) {
    refs.push({ sourceId: `goal-source-${sourceKey}`, sourceTitle: SAFE_SOURCE_LABELS[sourceKey] });
  }
  return normalizeChapterProvenance(refs, 'goal-source');
};

const buildExplanation = (goal = {}, state = 'current') => {
  const context = cleanText(goal.description || goal.successCriteria);
  if (context) return context;
  if (state === 'completed') return 'This confirmed goal has been completed.';
  if (state === 'stopped') return 'This confirmed goal is no longer active.';
  if (state === 'temporary') return 'This is a confirmed temporary care objective.';
  if (state === 'aspiration') return 'This is a confirmed long-term aspiration.';
  return 'This is a confirmed current goal.';
};

const buildEntry = (goal, state, idPrefix = 'entry') => ({
  id: `${idPrefix}-${goal.id}`,
  headline: cleanText(goal.title),
  explanation: buildExplanation(goal, state),
  sourceAction: goal.id ? {
    actionId: goal.id,
    ownership: 'caregiver_fact',
    isEditable: true,
    actionLabel: 'Edit goal',
  } : undefined,
  responseGuidance: cleanText(goal.supportStrategy) ? {
    label: 'How caregivers can help',
    text: cleanText(goal.supportStrategy),
  } : undefined,
  provenance: getGoalProvenance(goal),
});

const getGroupOrder = (lens) => {
  const history = ['completed_goals', 'no_longer_active'];
  if (lens === CARE_LENS.AUTISM) return ['current_priorities', 'communication_participation', 'daily_living_independence', 'health_comfort_wellbeing', 'temporary_objectives', 'long_term_aspirations', ...history];
  if (lens === CARE_LENS.DEMENTIA) return ['current_priorities', 'health_comfort_wellbeing', 'daily_living_independence', 'communication_participation', 'long_term_aspirations', 'temporary_objectives', ...history];
  if (lens === CARE_LENS.HOSPICE) return ['current_priorities', 'health_comfort_wellbeing', 'long_term_aspirations', 'communication_participation', 'temporary_objectives', 'daily_living_independence', ...history];
  return DEFAULT_GROUP_ORDER;
};

const priorityScore = ({ goal, state }) => (
  (cleanText(goal.supportStrategy) ? 100 : 0)
  + (state === 'temporary' ? 80 : 0)
  + (goal.isPriority || goal.currentPriority || goal.priority === true ? 60 : 0)
  + (SAFE_SOURCE_LABELS[cleanText(goal.source).toLowerCase()] ? 20 : 0)
  + (state === 'aspiration' ? -10 : 0)
);

const deduplicateGoals = (goals = []) => {
  const seen = new Set();
  return goals.filter((goal) => {
    const key = goalValueKey(goal);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const buildGoalsDomain = ({
  child = {},
  goals = child.supportProfile?.careGoals || [],
  careContext = child.careContext || {},
  ageGroup = careContext?.ageGroup || '',
} = {}) => {
  const sourceGoals = Array.isArray(goals) ? goals : [];
  const eligible = deduplicateGoals(sourceGoals.filter(isConfirmedGoal)).map((goal, index) => ({
    goal,
    state: resolveGoalState(goal),
    index,
  }));
  const current = eligible.filter((item) => ['current', 'temporary', 'aspiration'].includes(item.state))
    .sort((left, right) => priorityScore(right) - priorityScore(left) || left.index - right.index);
  const priorities = current.slice(0, 3);
  const lens = getCareLensKey({ selectedConditions: child.conditions || [], careContext: careContext || child.careContext || {} });
  const groupOrder = getGroupOrder(lens);
  const itemsByGroup = Object.fromEntries(groupOrder.map((groupId) => [groupId, []]));
  priorities.forEach((item) => itemsByGroup.current_priorities.push(item));
  eligible.forEach((item) => {
    const groupId = item.state === 'completed'
      ? 'completed_goals'
      : item.state === 'stopped'
        ? 'no_longer_active'
        : classifyCurrentGoal(item.goal, item.state);
    if (groupId && itemsByGroup[groupId]) itemsByGroup[groupId].push(item);
  });
  const groups = groupOrder.map((groupId) => ({
    id: groupId,
    title: GROUPS[groupId],
    entries: itemsByGroup[groupId].map(({ goal, state }) => buildEntry(
      goal,
      state,
      groupId === 'current_priorities' ? 'entry-priority' : 'entry'
    )),
  })).filter((group) => group.entries.length);
  const provenance = priorities.map((item) => getGoalProvenance(item.goal)).find(Boolean);

  return {
    id: 'goals',
    title: PROFILE_CHAPTER_TITLES.GOALS,
    caregiverQuestion: 'What are we working toward?',
    briefing: {
      items: priorities.map(({ goal }) => ({
        id: `briefing-${goal.id}`,
        text: cleanText(goal.title),
        explainedByEntryIds: [`entry-${goal.id}`],
      })),
      provenance,
    },
    handbook: { groups },
    emptyState: {
      title: 'What are we working toward?',
      description: 'Add confirmed goals, temporary care objectives, or long-term aspirations that caregivers can support.',
    },
    metadata: {
      domainId: GOALS_DOMAIN_ID,
      careFocus: lens,
      ageGroup: ageGroup || careContext?.ageGroup || 'unknown',
      contractVersion: '1',
    },
  };
};
